// RTK async thunks for residual risk operations (assessment-centric)
import {
  generateResidualRisk,
  analyzeActionPlan,
  saveResidualRisk,
  RESIDUAL_RISK_RATINGS,
} from '../../redux/slices/residualRiskSlice';

export class ResidualRiskService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeResidualAssessment(areaName, riskFormData, riskModel, controlState, progressCallback) {
    try {
      console.log(`[ResidualRiskService] Starting residual assessment for area: ${areaName}`);
      if (progressCallback) progressCallback('residual');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state - check RTK assessment slice first, fallback to legacy
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Assessment-centric approach: Read from RTK slice (riskItems), fallback to legacy (risks)
      // RTK slice uses: state.riskItems.byAssessment[assessmentUuid][areaName].areaRisks
      // Legacy uses: state.risks.byAssessment[assessmentUuid].modelRisks[areaName].areaRisks
      const rtkRisksState = currentState.riskItems?.byAssessment?.[this.assessmentUuid];
      const legacyRisksState = currentState.risks?.byAssessment?.[this.assessmentUuid];

      // Get area risks - RTK first (with modelRisks wrapper), then legacy fallback
      const rtkAreaRisks = rtkRisksState?.modelRisks?.[areaName]?.areaRisks || {};
      const legacyAreaRisks = legacyRisksState?.modelRisks?.[areaName]?.areaRisks || {};
      const areaRisks = Object.keys(rtkAreaRisks).length > 0 ? rtkAreaRisks : legacyAreaRisks;

      const evaluatedRisks = [];

      console.log(`[ResidualRiskService] 🔍 State source for area: ${areaName}`);
      console.log(`[ResidualRiskService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[ResidualRiskService] Available risk sources:`, Object.keys(areaRisks));
      Object.entries(areaRisks).forEach(([sourceName, riskArray]) => {
        if (Array.isArray(riskArray)) {
          riskArray.forEach(risk => {
            if (risk.status === 'Saved' && risk.priority) {
              evaluatedRisks.push({
                ...risk,
                areaName,
                sourceName
              });
            }
          });
        }
      });
      
      console.log(`[ResidualRiskService] Found ${evaluatedRisks.length} evaluated risks`);

      // Get fresh control state instead of stale controlState parameter
      const freshControlState = currentState.controls || {};
      
      // Find risks with saved actions from fresh control state
      console.log(`[ResidualRiskService] Checking for saved controls...`);
      console.log(`[ResidualRiskService] Available savedControls keys:`, Object.keys(freshControlState?.savedControls || {}));
      
      const risksWithActions = evaluatedRisks.filter(risk => {
        // Double-check: ensure this risk is actually from the current area
        if (risk.areaName !== areaName) {
          console.log(`[ResidualRiskService] ⚠️ SKIPPING risk from wrong area: "${risk.riskName}" (area: ${risk.areaName}, expected: ${areaName})`);
          return false;
        }
        
        const allControls = freshControlState?.savedControls?.[risk.riskId] || [];
        const modelControls = allControls.filter(control => control.modelId === this.modelId);
        console.log(`[ResidualRiskService] Risk "${risk.riskName}" (${risk.riskId}): ${modelControls.length} saved controls for this model (${allControls.length} total)`);
        return modelControls.length > 0;
      });
      
      console.log(`[ResidualRiskService] Found ${risksWithActions.length} risks with actions for residual assessment`);

      let residualAssessments = 0;

      for (const risk of risksWithActions) {
        try {
          // PARALLEL EXECUTION: Run both API calls concurrently for ~40-50% speed improvement
          // Neither thunk depends on the other's output - they read independently from Redux state
          console.log(`[ResidualRiskService] ⚡ Starting parallel assessment for: ${risk.riskName}`);
          const startTime = Date.now();

          await Promise.all([
            // Analyze action plan effectiveness (RTK async thunk)
            this.dispatch(analyzeActionPlan({
              assessmentUuid: this.assessmentUuid,
              modelId: this.modelId,
              riskId: risk.riskId,
              riskName: risk.riskName
            })),
            // Generate residual risk assessment (RTK async thunk)
            this.dispatch(generateResidualRisk({
              assessmentUuid: this.assessmentUuid,
              modelId: this.modelId,
              riskId: risk.riskId
            }))
          ]);

          const elapsed = Date.now() - startTime;
          console.log(`[ResidualRiskService] ✓ Parallel assessment completed in ${elapsed}ms for: ${risk.riskName}`);

          // LIVE UPDATE: Stream action plan analysis completion (after parallel completes)
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('actionPlanAnalyzed', {
              riskName: risk.riskName,
              riskId: risk.riskId
            });
          }

          // Get the generated residual risk data from RTK slice (assessment-centric)
          const currentState = getState();
          const residualRiskData = currentState.residualRiskRTK?.byAssessment?.[this.assessmentUuid]?.[risk.riskId] || {};

          // Validate rating is in enum - if invalid, set to null and skip save
          let residualRating = residualRiskData.residual_rating;
          if (!residualRating || !RESIDUAL_RISK_RATINGS.includes(residualRating)) {
            console.error(`[ResidualRiskService] Invalid/missing rating "${residualRating}" - skipping save for this risk`);
            // Skip saving this risk - don't use a misleading default
            continue;
          }

          // Save residual risk data with validated rating
          const residualData = {
            modelId: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskId: risk.riskId,
            riskName: risk.riskName,
            priority: risk.priority,
            evaluator_id: 'autopilot@agenthuginn.ai',
            comments: `Generated by RiskAutopilotV2 for area: ${areaName}`,
            residual_rating: residualRating,
            residual_explanation: residualRiskData.residual_explanation || '',
            action_plan_rating: residualRiskData.action_plan_rating || '',
            action_plan_analysis: residualRiskData.action_plan_analysis || '',
            action_plan_short_summary: residualRiskData.action_plan_short_summary || ''
          };

          this.dispatch(saveResidualRisk(residualData));
          residualAssessments++;
          
          // LIVE UPDATE: Stream residual risk assessment completion with rating
          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('residualRiskCalculated', {
              riskName: risk.riskName,
              riskId: risk.riskId,
              residualRating: residualRating, // Include the actual rating for display
              actionPlanRating: residualRiskData.action_plan_rating || '',
              assessedCount: residualAssessments,
              totalRisks: risksWithActions.length
            });
          }
          
        } catch (residualError) {
          console.error(`[ResidualRiskService] Residual assessment failed for ${risk.riskName}:`, residualError);
        }
      }

      return {
        risksWithActions: risksWithActions.length,
        residualAssessments,
        areaName
      };

    } catch (error) {
      console.error('[ResidualRiskService] Residual assessment failed:', error);
      throw error;
    }
  }
}